import { Link } from "react-router-dom";
import { useState, type FormEvent } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useLang, useT } from "../i18n";
import {
  isHubSpotConfigured,
  submitContactForm,
  type ContactLeadSource,
} from "../lib/hubspot";
import { btnPrimary, cardClass, labelText } from "../lib/ui";

const inputClass =
  "w-full rounded-xl border border-[var(--border-subtle)] bg-surface-light px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground transition focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/25";

const checkboxClass =
  "mt-0.5 h-4 w-4 shrink-0 rounded border border-[var(--border-subtle)] bg-surface-light text-accent focus:ring-2 focus:ring-accent/25";

interface ContactFormProps {
  source: ContactLeadSource;
}

type FormStatus = "idle" | "submitting" | "success" | "error";

interface FormFields {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  phone: string;
  message: string;
  website: string;
}

const EMPTY_FIELDS: FormFields = {
  firstName: "",
  lastName: "",
  email: "",
  company: "",
  phone: "",
  message: "",
  website: "",
};

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

export default function ContactForm({ source }: ContactFormProps) {
  const translate = useT();
  const { lang } = useLang();
  const [fields, setFields] = useState<FormFields>(EMPTY_FIELDS);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [status, setStatus] = useState<FormStatus>("idle");
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof FormFields, string>>>({});
  const [privacyError, setPrivacyError] = useState<string | undefined>();

  function updateField<K extends keyof FormFields>(key: K, value: FormFields[K]) {
    setFields((current) => ({ ...current, [key]: value }));
    if (fieldErrors[key]) {
      setFieldErrors((current) => ({ ...current, [key]: undefined }));
    }
  }

  function validate(): boolean {
    const errors: Partial<Record<keyof FormFields, string>> = {};
    let privacyValid = true;

    if (!fields.firstName.trim()) {
      errors.firstName = translate("contactErrorFirstNameRequired");
    }
    if (!fields.lastName.trim()) {
      errors.lastName = translate("contactErrorLastNameRequired");
    }
    if (!fields.email.trim()) {
      errors.email = translate("contactErrorEmailRequired");
    } else if (!isValidEmail(fields.email.trim())) {
      errors.email = translate("contactErrorEmailInvalid");
    }
    if (!fields.company.trim()) {
      errors.company = translate("contactErrorCompanyRequired");
    }
    if (!privacyAccepted) {
      setPrivacyError(translate("contactErrorPrivacyRequired"));
      privacyValid = false;
    } else {
      setPrivacyError(undefined);
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0 && privacyValid;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (fields.website) {
      setStatus("success");
      return;
    }

    if (!validate()) {
      return;
    }

    if (!isHubSpotConfigured()) {
      setStatus("error");
      return;
    }

    setStatus("submitting");

    try {
      await submitContactForm({
        firstName: fields.firstName.trim(),
        lastName: fields.lastName.trim(),
        email: fields.email.trim(),
        company: fields.company.trim(),
        phone: fields.phone.trim() || undefined,
        message: fields.message.trim() || undefined,
        leadSource: source,
        language: lang,
        privacyConsentText: translate("contactConsentPrivacyLegal"),
        oneToOneConsentText: translate("contactConsentOneToOneLegal"),
        marketingConsent,
        marketingConsentText: translate("contactConsentMarketingLegal"),
      });
      setFields(EMPTY_FIELDS);
      setPrivacyAccepted(false);
      setMarketingConsent(false);
      setStatus("success");
    } catch {
      setStatus("error");
    }
  }

  if (status === "success") {
    return (
      <div className={`${cardClass} flex flex-col items-center gap-3 p-8 text-center`}>
        <CheckCircle2 className="h-10 w-10 text-success" aria-hidden />
        <p className="font-display text-lg font-semibold">{translate("contactSuccessTitle")}</p>
        <p className="text-sm text-muted-foreground">{translate("contactSuccessBody")}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className={`${cardClass} relative space-y-5 p-6 sm:p-8`} noValidate>
      <div className="grid gap-5 sm:grid-cols-2">
        <div className="space-y-1.5">
          <label htmlFor={`${source}-first-name`} className={labelText}>
            {translate("contactLabelFirstName")}
          </label>
          <input
            id={`${source}-first-name`}
            type="text"
            autoComplete="given-name"
            className={inputClass}
            placeholder={translate("contactPlaceholderFirstName")}
            value={fields.firstName}
            onChange={(event) => updateField("firstName", event.target.value)}
            aria-invalid={Boolean(fieldErrors.firstName)}
            aria-describedby={fieldErrors.firstName ? `${source}-first-name-error` : undefined}
          />
          {fieldErrors.firstName ? (
            <p id={`${source}-first-name-error`} className="text-xs text-error" role="alert">
              {fieldErrors.firstName}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <label htmlFor={`${source}-last-name`} className={labelText}>
            {translate("contactLabelLastName")}
          </label>
          <input
            id={`${source}-last-name`}
            type="text"
            autoComplete="family-name"
            className={inputClass}
            placeholder={translate("contactPlaceholderLastName")}
            value={fields.lastName}
            onChange={(event) => updateField("lastName", event.target.value)}
            aria-invalid={Boolean(fieldErrors.lastName)}
            aria-describedby={fieldErrors.lastName ? `${source}-last-name-error` : undefined}
          />
          {fieldErrors.lastName ? (
            <p id={`${source}-last-name-error`} className="text-xs text-error" role="alert">
              {fieldErrors.lastName}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <label htmlFor={`${source}-email`} className={labelText}>
            {translate("contactLabelEmail")}
          </label>
          <input
            id={`${source}-email`}
            type="email"
            autoComplete="email"
            className={inputClass}
            placeholder={translate("contactPlaceholderEmail")}
            value={fields.email}
            onChange={(event) => updateField("email", event.target.value)}
            aria-invalid={Boolean(fieldErrors.email)}
            aria-describedby={fieldErrors.email ? `${source}-email-error` : undefined}
          />
          {fieldErrors.email ? (
            <p id={`${source}-email-error`} className="text-xs text-error" role="alert">
              {fieldErrors.email}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5">
          <label htmlFor={`${source}-phone`} className={labelText}>
            {translate("contactLabelPhone")}
          </label>
          <input
            id={`${source}-phone`}
            type="tel"
            autoComplete="tel"
            className={inputClass}
            placeholder={translate("contactPlaceholderPhone")}
            value={fields.phone}
            onChange={(event) => updateField("phone", event.target.value)}
          />
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <label htmlFor={`${source}-company`} className={labelText}>
            {translate("contactLabelCompany")}
          </label>
          <input
            id={`${source}-company`}
            type="text"
            autoComplete="organization"
            className={inputClass}
            value={fields.company}
            onChange={(event) => updateField("company", event.target.value)}
            aria-invalid={Boolean(fieldErrors.company)}
            aria-describedby={fieldErrors.company ? `${source}-company-error` : undefined}
          />
          {fieldErrors.company ? (
            <p id={`${source}-company-error`} className="text-xs text-error" role="alert">
              {fieldErrors.company}
            </p>
          ) : null}
        </div>

        <div className="space-y-1.5 sm:col-span-2">
          <label htmlFor={`${source}-message`} className={labelText}>
            {translate("contactLabelMessage")}
          </label>
          <textarea
            id={`${source}-message`}
            rows={4}
            className={`${inputClass} resize-y min-h-[6rem]`}
            placeholder={translate("contactPlaceholderMessage")}
            value={fields.message}
            onChange={(event) => updateField("message", event.target.value)}
          />
        </div>
      </div>

      <fieldset className="space-y-3 border-0 p-0">
        <legend className="sr-only">Consent</legend>

        <label className="flex items-start gap-3 text-sm leading-relaxed text-foreground">
          <input
            id={`${source}-privacy-consent`}
            type="checkbox"
            className={checkboxClass}
            checked={privacyAccepted}
            onChange={(event) => {
              setPrivacyAccepted(event.target.checked);
              if (event.target.checked) {
                setPrivacyError(undefined);
              }
            }}
            aria-invalid={Boolean(privacyError)}
            aria-describedby={privacyError ? `${source}-privacy-error` : undefined}
            required
          />
          <span>
            {translate("contactConsentPrivacyBefore")}
            <Link
              to="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-accent underline underline-offset-2 hover:text-accent/80"
            >
              {translate("contactConsentPrivacyLink")}
            </Link>
            {translate("contactConsentPrivacyAfter")}
          </span>
        </label>
        {privacyError ? (
          <p id={`${source}-privacy-error`} className="text-xs text-error" role="alert">
            {privacyError}
          </p>
        ) : null}

        <label className="flex items-start gap-3 text-sm leading-relaxed text-muted-foreground">
          <input
            id={`${source}-marketing-consent`}
            type="checkbox"
            className={checkboxClass}
            checked={marketingConsent}
            onChange={(event) => setMarketingConsent(event.target.checked)}
          />
          <span>{translate("contactConsentMarketing")}</span>
        </label>
      </fieldset>

      <div className="absolute -left-[9999px]" aria-hidden>
        <label htmlFor={`${source}-website`}>Website</label>
        <input
          id={`${source}-website`}
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={fields.website}
          onChange={(event) => updateField("website", event.target.value)}
        />
      </div>

      {status === "error" ? (
        <p className="text-sm text-error" role="alert">
          {translate(
            isHubSpotConfigured() ? "contactErrorSubmit" : "contactErrorNotConfigured",
          )}
        </p>
      ) : null}

      <button type="submit" className={`${btnPrimary} w-full sm:w-auto`} disabled={status === "submitting"}>
        {status === "submitting" ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            {translate("contactSubmitting")}
          </>
        ) : (
          translate("contactSubmit")
        )}
      </button>
    </form>
  );
}
